import React from 'react';
import { defaultStyles, FileIcon } from 'react-file-icon';
import classnames from 'classnames';
import qs from 'query-string';

import FileUtil from '@utils/FileUtil';

import { IDisplayEditAttachment } from '../Attachment/AttachmentsEditModal';

import DownloadButton from './DownloadAttachment';
import GoogleDocsIFrame from './GoogleDocsIframe';
import MSDocIframe from './MSIframe';
import PdfViewer from './PdfViewer';
import XlsxViewer from './XlsxViewer';

import './style.scss';

export interface IDocumentViewerProps {
  attachment: IDisplayEditAttachment;
}

export interface IDocumentViewerState {
  showFallbackComponent: boolean;
  isFileTypeSupported: boolean;
}

const SUPPORTED_FILE_TYPES = ['doc', 'docx', 'jpeg', 'jpg', 'pdf', 'png', 'ppt', 'pptx', 'rtf', 'txt', 'xls', 'xlsx'];

const SLASH_PLACEHOLDER = ':::';
class DocViewer extends React.Component<IDocumentViewerProps, IDocumentViewerState> {
  constructor(props: IDocumentViewerProps) {
    super(props);
    const isFileTypeSupported = this.isFileTypeSupported(this.props.attachment);
    this.state = {
      showFallbackComponent: !isFileTypeSupported,
      isFileTypeSupported,
    };
  }

  isFileTypeSupported = (attachment: IDisplayEditAttachment): boolean => {
    if (attachment) {
      const { fileURL } = this.props.attachment;
      const fileExtension = FileUtil.getFileExtension(fileURL);

      return SUPPORTED_FILE_TYPES.includes(fileExtension);
    }

    return false;
  };

  onError = () => {
    this.setState({
      showFallbackComponent: true,
    });
  };

  // TODO - get file size in bytes in api response
  isValidMSIframeFileSize = (file: IAttachment): boolean => {
    const [dimension, unit] = file.fileSize.split(' ');
    if (unit === 'kB') {
      return parseInt(dimension, 10) < 5120;
    }
    if (unit === 'MB') {
      return parseInt(dimension, 10) < 5;
    }

    return true;
  };

  replaceSlashes(str: string) {
    if (!str) {
      return str;
    }

    return str.replace(/\//g, SLASH_PLACEHOLDER);
  }

  render() {
    const { attachment } = this.props;
    let redirectedUrl = attachment.presignedUrl || attachment.fileURL;

    if (redirectedUrl && redirectedUrl.length) {
      const params = qs.parse(redirectedUrl.split('?')[1]);
      const domain = redirectedUrl.split('://')[1].split('/')[0];
      let fileKey = redirectedUrl.split('://')[1].split('.com')[1].split('?')[0];

      fileKey = this.replaceSlashes(fileKey.slice(1, fileKey.length));
      const signedHeaders = this.replaceSlashes(params['X-Amz-SignedHeaders']);
      const credential = this.replaceSlashes(params['X-Amz-Credential']);
      const signature = this.replaceSlashes(params['X-Amz-Signature'] || params.Signature);
      const date = this.replaceSlashes(params['X-Amz-Date']);

      const accessKeyId = params.AWSAccessKeyId;
      const expires = params['X-Amz-Expires'] || params.Expires;
      const algorithm = params['X-Amz-Algorithm'];

      redirectedUrl = `${process.env.API_URL}/s3-redirect/${domain}/${algorithm}/${
        credential
        // tslint:disable-next-line: max-line-length
      }/${date}/${expires}/${encodeURIComponent(signature)}/${accessKeyId}/${signedHeaders}/${fileKey}`;
    }

    const fileExtension: string = FileUtil.getFileExtension(attachment.fileURL);

    if (this.state.showFallbackComponent) {
      const errorMsg =
        // tslint:disable-next-line: max-line-length
        'Something went wrong while loading attachment. Please download the attachment from the link below.';
      // tslint:disable-next-line: max-line-length
      const unsupportedFileMsg =
        // tslint:disable-next-line: max-line-length
        'File type unsupported. Please download the attachment from the link below';
      const fallbackMsg = this.state.isFileTypeSupported ? errorMsg : unsupportedFileMsg;

      return (
        <>
          <div className={classnames('document-view-fallback attachments-tile__saved-thumb')}>
            <FileIcon extension={fileExtension} {...defaultStyles[fileExtension]} />
          </div>
          <DownloadButton attachment={attachment} errMsg={fallbackMsg} />
        </>
      );
    }

    let renderer;
    switch (fileExtension) {
      case 'ppt':
      case 'pptx':
      case 'doc':
      case 'docx':
        renderer = <MSDocIframe fileURL={redirectedUrl} onError={this.onError} />;
        break;
      case 'xls':
      case 'xlsx':
        renderer = <XlsxViewer attachment={attachment} />;
        break;
      case 'pdf':
        renderer = <PdfViewer attachment={attachment} />;
        break;
      default:
        renderer = <GoogleDocsIFrame fileURL={redirectedUrl} onError={this.onError} />;
        break;
    }

    return <div className={'doc-viewer'}>{renderer}</div>;
  }
}

export interface IDocViewIFrameProps {
  onError: () => void;
  fileURL: string;
}

export default DocViewer;
